import { Group, Select, Stack, TextInput, Tooltip } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconArrowRight, IconPlus, IconTrash } from '@tabler/icons-react';
import P3Regular from 'components/typography/P3Regular';
import { useFormInputs } from 'pages/revisions-module/template-editor/editors/form/input/FormInputsDataProvider';
import { IFormInputContext, IFormInputSpec } from 'pages/revisions-module/template-editor/editors/form/types';
import { useCallback, useMemo } from 'react';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

interface ValueMapping {
  source: string;
  target: string;
}

interface ValueMappingsRepeaterProps {
  value?: ValueMapping[];
  onChange?: (value: ValueMapping[]) => void;
}

const EMPTY_PAIR: ValueMapping = { source: '', target: '' };

/**
 * Renders a repeater used to configure source-target mappings.
 */
export function ValueMappingsRepeater({
  value = [EMPTY_PAIR],
  onChange = () => undefined,
}: ValueMappingsRepeaterProps) {
  const pairs = value.length > 0 ? value : [EMPTY_PAIR];

  /** Updates a specific mapping pair. */
  const updatePair = (index: number, key: keyof ValueMapping, nextValue: string) => {
    const nextPairs = [...pairs];
    nextPairs[index] = { ...nextPairs[index], [key]: nextValue };
    onChange(nextPairs);
  };

  /** Removes the mapping at the specified index. */
  const removePair = (index: number) => {
    if (pairs.length === 1) {
      onChange([EMPTY_PAIR]);
      return;
    }

    const nextPairs = [...pairs];
    nextPairs.splice(index, 1);
    onChange(nextPairs);
  };

  /** Appends a new empty mapping. */
  const addPair = () => onChange([...pairs, { ...EMPTY_PAIR }]);

  return (
    <Stack spacing={8}>
      <P3Regular color="gray.6">Mapovania hodnôt</P3Regular>
      {pairs.map((pair, index) => (
        <Group key={`pair-${index}`} spacing={8} align="center">
          <TextInput
            placeholder="Zdrojová hodnota"
            sx={{ flex: 1 }}
            value={pair.source}
            onChange={(event) => updatePair(index, 'source', event.currentTarget.value)}
          />
          <IconArrowRight size={20} />
          <TextInput
            placeholder="Cieľová hodnota"
            sx={{ flex: 1 }}
            value={pair.target}
            onChange={(event) => updatePair(index, 'target', event.currentTarget.value)}
          />
          <Tooltip label="Odstrániť mapovanie" withArrow={false} openDelay={300}>
            <ActionIcon w={32} variant="subtle-gray" size="sm" onClick={() => removePair(index)}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ))}
      <Button variant="secondary" size="sm" leftIcon={<IconPlus />} mr={40} onClick={addPair}>
        Pridať mapovanie
      </Button>
    </Stack>
  );
}

interface ValueMapInputPropertiesSubFormProps {
  form: UseFormReturnType<IFormInputSpec>;
  context: IFormInputContext;
}

/**
 * Property editor for the value map input type.
 */
export default function ValueMapInputPropertiesSubForm({ form, context }: ValueMapInputPropertiesSubFormProps) {
  const { allInputs } = useFormInputs();

  const sourceOptions = useMemo(
    () =>
      allInputs
        .filter((input) => input.context === context && input.spec.name !== form.values.name)
        .map(({ spec }) => ({ value: spec.name, label: spec.label })),
    [allInputs, context, form.values.name]
  );

  const mappingsValue = ((form.values as IFormInputSpec & { mappings?: ValueMapping[] }).mappings ?? [
    EMPTY_PAIR,
  ]) as ValueMapping[];

  const setMappings = useCallback((value: ValueMapping[]) => form.setFieldValue('mappings', value), [form]);

  return (
    <Stack spacing={12}>
      <Select
        label="Zdrojové pole"
        placeholder={sourceOptions.length === 0 ? 'Najprv vytvorte iné pole v tejto sekcii' : undefined}
        searchable
        data={sourceOptions}
        nothingFound="Žiadne pole"
        disabled={sourceOptions.length === 0}
        {...form.getInputProps('sourceInput')}
      />
      <ValueMappingsRepeater value={mappingsValue} onChange={setMappings} />
      <TextInput label="Predvolená hodnota (voliteľné)" {...form.getInputProps('defaultValue')} />
    </Stack>
  );
}
